import { zodResolver } from "@hookform/resolvers/zod";
import { notifications } from "@marketplace/ui/notifications";
import { useAtomValue } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useEffect } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { sendAccessModalAtom } from "../../atoms/send-access-atom";
import { usePutSendCredentialsMutation } from "../mutations/use-put-send-credentials-mutation";
import {
  type SendAccessFormSchema,
  useSendAccessFormSchema
} from "../schemas/use-send-access-form-schema";

export const useSendAccessForm = () => {
  const { t } = useTranslation("order", {
    keyPrefix: "orderListPage"
  });
  const schema = useSendAccessFormSchema();
  const { mutateAsync, isPending } = usePutSendCredentialsMutation();
  const { data: orderData } = useAtomValue(sendAccessModalAtom);
  const resetSendAccessModal = useResetAtom(sendAccessModalAtom);

  const { register, handleSubmit, formState, reset, control, watch } =
    useForm<SendAccessFormSchema>({
      resolver: zodResolver(schema),
      defaultValues: {
        credentials: ""
      }
    });

  useEffect(() => {
    if (!orderData) return;

    reset({
      credentials: orderData.credentials
    });
  }, [orderData, reset]);

  const onSubmit: SubmitHandler<SendAccessFormSchema> = async (data) => {
    try {
      if (!orderData) return;

      const res = await mutateAsync({
        credentials: data.credentials,
        orderItemId: orderData?.id
      });

      if (res) {
        reset();
        notifications.show({
          message: t("sendAccessModal.notifications.sendAccessSuccess")
        });
        resetSendAccessModal();
      }
    } catch {
      notifications.show({
        variant: "error",
        message: t("sendAccessModal.notifications.sendAccessFailed")
      });
    }
  };

  return {
    register,
    formState,
    handleSubmit: handleSubmit(onSubmit),
    isPending,
    control,
    watch
  };
};
